import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, CreditCard, ShoppingBag, Settings, Shield, TrendingUp,
  Check, X, Loader2, Plus, Trash2, Eye, EyeOff, ChevronLeft, Upload, LogOut, Search, MessageCircle
} from "lucide-react";

import { API } from "@/apiConfig";

function formatApiError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// Admin Login
function AdminLogin({ onLogin }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { phone, password });
      if (data.role !== "admin") {
        setError("Admin access required");
        return;
      }
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      onLogin(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page" data-testid="admin-login">
      <div className="auth-card p-8 w-full max-w-md mx-auto">
        <h2 className="heading-serif text-2xl text-white text-center mb-6">Admin Panel</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="auth-label">Phone Number</label>
            <input type="tel" className="auth-input" value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="Admin phone" data-testid="admin-phone-input" />
          </div>
          <div>
            <label className="auth-label">Password</label>
            <input type="password" className="auth-input" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Admin password" data-testid="admin-password-input" />
          </div>
          {error && <p className="error-text" data-testid="admin-login-error">{error}</p>}
          <button type="submit" className="btn-gold" disabled={loading} data-testid="admin-login-btn">
            {loading ? <Loader2 size={18} className="animate-spin mx-auto" /> : "Login to Admin"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Sidebar navigation items
const navItems = [
  { id: "users", label: "User Management", icon: Users },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "products", label: "Products", icon: ShoppingBag },
  { id: "methods", label: "Payment Methods", icon: CreditCard },
  { id: "settings", label: "Settings", icon: Settings },
  { id: "other", label: "Other Settings", icon: MessageCircle },
  { id: "credentials", label: "Admin Credentials", icon: Shield },
  { id: "referrers", label: "Top Referrers", icon: TrendingUp },
];

// User Management Tab
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [addFundsUser, setAddFundsUser] = useState(null);
  const [fundAmount, setFundAmount] = useState("");
  const [fundDesc, setFundDesc] = useState("");
  const [fundLoading, setFundLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { fetchUsers(); }, []);
  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data);
    } catch { } finally { setLoading(false); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    try {
      const { data } = await axios.get(`${API}/admin/users-search?q=${searchQuery}`);
      setSearchResults(data);
    } catch { }
  };

  const viewUserDetail = async (userId) => {
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}`);
      setUserDetail(data);
      setSelectedUser(userId);
    } catch (e) { toast.error("Failed to load user details"); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`User ${userId} deleted`);
      setDeleteConfirm(null);
      setSelectedUser(null);
      fetchUsers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleAddFunds = async () => {
    const amt = parseFloat(fundAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setFundLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/users/${addFundsUser}/add-funds`, {
        amount: amt, description: fundDesc || ""
      });
      toast.success(data.message);
      setAddFundsUser(null);
      setFundAmount("");
      setFundDesc("");
      fetchUsers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFundLoading(false); }
  };

  const displayUsers = searchResults || users;

  if (loading) return <Loader2 className="animate-spin text-[#d4af37] mx-auto mt-8" />;
  return (
    <div data-testid="admin-users-tab">
      <h2 className="text-xl font-bold text-white mb-4">User Management ({users.length})</h2>

      {/* Search */}
      <div className="flex gap-2 mb-4" data-testid="user-search">
        <input className="auth-input flex-1" placeholder="Search by phone, user ID, or email..."
          value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSearch()} data-testid="user-search-input" />
        <button className="btn-gold" style={{width: "auto", padding: "8px 16px"}} onClick={handleSearch} data-testid="user-search-btn">
          <Search size={16} />
        </button>
        {searchResults && (
          <button className="admin-filter-btn" onClick={() => { setSearchResults(null); setSearchQuery(""); }}>Clear</button>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && userDetail && (
        <div className="user-detail-overlay" onClick={() => setSelectedUser(null)}>
          <div className="user-detail-modal" onClick={e => e.stopPropagation()} data-testid="user-detail-modal">
            <button className="about-popup-close" onClick={() => setSelectedUser(null)}><X size={18} /></button>
            <h3 className="text-lg font-bold text-white mb-1">User Details - ID: {userDetail.user_id}</h3>
            <p className="text-sm text-slate-400 mb-4">{userDetail.phone} | {userDetail.email || "No email"}</p>

            <div className="admin-stats-grid mb-4">
              <div className="admin-stat-card"><p className="text-xs text-slate-400">Balance</p><p className="text-lg font-bold text-[#d4af37]">Rs.{userDetail.balance?.toFixed(2)}</p></div>
              <div className="admin-stat-card"><p className="text-xs text-slate-400">Invite Code</p><p className="text-sm font-bold text-white">{userDetail.invite_code}</p></div>
              <div className="admin-stat-card"><p className="text-xs text-slate-400">Role</p><p className="text-sm font-bold text-white">{userDetail.role}</p></div>
              <div className="admin-stat-card"><p className="text-xs text-slate-400">Joined</p><p className="text-xs text-white">{new Date(userDetail.created_at).toLocaleDateString()}</p></div>
            </div>

            {userDetail.bank_card && userDetail.bank_card.bank_account_name && (
              <div className="user-detail-section">
                <h4 className="text-sm font-bold text-white mb-2">Bank Card</h4>
                <p className="text-xs text-slate-400">Name: {userDetail.bank_card.bank_account_name}</p>
                <p className="text-xs text-slate-400">Account: {userDetail.bank_card.bank_account_number}</p>
                <p className="text-xs text-slate-400">IFSC: {userDetail.bank_card.bank_ifsc} | Bank: {userDetail.bank_card.bank_name}</p>
                {userDetail.bank_card.upi_id && <p className="text-xs text-slate-400">UPI: {userDetail.bank_card.upi_id}</p>}
              </div>
            )}

            {userDetail.purchases?.length > 0 && (
              <div className="user-detail-section">
                <h4 className="text-sm font-bold text-white mb-2">Purchases ({userDetail.purchases.length})</h4>
                {userDetail.purchases.map((p, i) => (
                  <div key={i} className="flex justify-between text-xs py-1 border-b border-white/5">
                    <span className="text-slate-300">{p.product_name}</span>
                    <span className="text-[#d4af37]">Rs.{p.price}</span>
                  </div>
                ))}
              </div>
            )}

            {userDetail.referrals?.length > 0 && (
              <div className="user-detail-section">
                <h4 className="text-sm font-bold text-white mb-2">Referrals ({userDetail.referrals.length})</h4>
                {userDetail.referrals.map((r, i) => (
                  <div key={i} className="flex justify-between text-xs py-1 border-b border-white/5">
                    <span className="text-slate-300">ID: {r.user_id} ({r.masked_phone})</span>
                    <span className="text-slate-500">{new Date(r.joined_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}

            {userDetail.transactions?.length > 0 && (
              <div className="user-detail-section">
                <h4 className="text-sm font-bold text-white mb-2">Transactions ({userDetail.transactions.length})</h4>
                <div style={{maxHeight: 200, overflowY: "auto"}}>
                  {userDetail.transactions.slice(0, 30).map((t, i) => (
                    <div key={i} className="flex justify-between text-xs py-1 border-b border-white/5">
                      <span className="text-slate-300">{t.description}</span>
                      <span className={t.type === "withdraw" ? "text-red-400" : "text-green-400"}>Rs.{t.amount?.toFixed(2)}</span>
                      <span className={`status-badge-admin ${t.status}`}>{t.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="user-detail-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="user-detail-modal" onClick={e => e.stopPropagation()} style={{maxWidth: 420}} data-testid="delete-confirm-modal">
            <h3 className="text-lg font-bold text-white mb-3">Delete User {deleteConfirm}?</h3>
            <p className="text-sm text-slate-400 mb-4">This will permanently delete the user and ALL their data including transactions, purchases, and bank details. This cannot be undone.</p>
            <div className="flex gap-3">
              <button className="btn-gold flex-1" style={{background:"#ef4444"}} onClick={() => handleDeleteUser(deleteConfirm)} data-testid="confirm-delete-btn">Delete Everything</button>
              <button className="btn-gold flex-1" style={{background:"rgba(255,255,255,0.1)",color:"#fff"}} onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Funds Modal */}
      {addFundsUser && (
        <div className="user-detail-overlay" onClick={() => setAddFundsUser(null)}>
          <div className="user-detail-modal" onClick={e => e.stopPropagation()} style={{maxWidth: 420}} data-testid="add-funds-modal">
            <button className="about-popup-close" onClick={() => setAddFundsUser(null)}><X size={18} /></button>
            <h3 className="text-lg font-bold text-white mb-4">Add Funds to User {addFundsUser}</h3>
            <div className="space-y-3">
              <div>
                <label className="auth-label">Amount (Rs.)</label>
                <input type="number" className="auth-input" placeholder="Enter amount" value={fundAmount}
                  onChange={e => setFundAmount(e.target.value)} min="1" data-testid="fund-amount-input" />
              </div>
              <div>
                <label className="auth-label">Description (optional)</label>
                <input type="text" className="auth-input" placeholder="e.g. Bonus, Refund" value={fundDesc}
                  onChange={e => setFundDesc(e.target.value)} data-testid="fund-desc-input" />
              </div>
            </div>
            <button className="btn-gold mt-4" onClick={handleAddFunds} disabled={fundLoading} data-testid="fund-submit-btn">
              {fundLoading ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Add Funds"}
            </button>
          </div>
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User ID</th><th>Phone</th><th>Email</th><th>Balance</th><th>Deposits</th><th>Withdrawals</th><th>Referrals</th><th>Role</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayUsers.map((u, i) => (
              <tr key={i} data-testid={`admin-user-${i}`}>
                <td>{u.user_id}</td>
                <td>{u.phone}</td>
                <td>{u.email || "-"}</td>
                <td className="text-[#d4af37]">Rs.{u.balance?.toFixed(2)}</td>
                <td className="text-green-400">Rs.{(u.total_deposits || 0).toFixed(2)}</td>
                <td className="text-red-400">Rs.{(u.total_withdrawals || 0).toFixed(2)}</td>
                <td>{u.referrals_count || 0}</td>
                <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
                <td>
                  <div className="flex gap-1">
                    {u.user_id && (
                      <button className="admin-action-btn approve" onClick={() => viewUserDetail(u.user_id)} data-testid={`view-user-${i}`} title="View Details">
                        <Eye size={14} />
                      </button>
                    )}
                    {u.user_id && u.role !== "admin" && (
                      <>
                        <button className="admin-action-btn" style={{background:"rgba(59,130,246,0.15)",color:"#3b82f6"}} onClick={() => { setAddFundsUser(u.user_id); setFundAmount(""); setFundDesc(""); }} data-testid={`add-funds-${i}`} title="Add Funds">
                          <Plus size={14} />
                        </button>
                        <button className="admin-action-btn reject" onClick={() => setDeleteConfirm(u.user_id)} data-testid={`delete-user-${i}`} title="Delete User">
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Payments Tab
function PaymentsTab() {
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);

  const fetchTxns = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/transactions?`;
      if (filter) url += `status=${filter}&`;
      if (typeFilter) url += `txn_type=${typeFilter}`;
      const { data } = await axios.get(url);
      setTxns(data);
    } catch { } finally { setLoading(false); }
  }, [filter, typeFilter]);

  useEffect(() => { fetchTxns(); fetchStats(); }, [filter, typeFilter, fetchTxns]);
  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`);
      setStats(data);
    } catch { }
  };

  const handleAction = async (txnId, action) => {
    try {
      await axios.post(`${API}/admin/transactions/${txnId}/${action}`, {});
      toast.success(`Transaction ${action}d`);
      fetchTxns();
      fetchStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const viewReceipt = async (path) => {
    try {
      const resp = await axios.get(`${API}/files/${path}`, { responseType: "blob" });
      setReceiptUrl(URL.createObjectURL(resp.data));
    } catch { toast.error("Failed to load receipt"); }
  };

  return (
    <div data-testid="admin-payments-tab">
      <h2 className="text-xl font-bold text-white mb-4">Payment Management</h2>

      {/* Receipt Modal */}
      {receiptUrl && (
        <div className="receipt-modal" onClick={() => { URL.revokeObjectURL(receiptUrl); setReceiptUrl(null); }} data-testid="receipt-modal">
          <img src={receiptUrl} alt="Payment Receipt" />
        </div>
      )}
      {stats && (
        <div className="admin-stats-grid mb-4">
          <div className="admin-stat-card"><p className="text-xs text-slate-400">Total Deposits</p><p className="text-lg font-bold text-green-400">Rs.{stats.total_deposit_amount?.toFixed(2)}</p></div>
          <div className="admin-stat-card"><p className="text-xs text-slate-400">Total Withdrawals</p><p className="text-lg font-bold text-red-400">Rs.{stats.total_withdrawal_amount?.toFixed(2)}</p></div>
          <div className="admin-stat-card"><p className="text-xs text-slate-400">Pending Deposits</p><p className="text-lg font-bold text-yellow-400">{stats.pending_deposits}</p></div>
          <div className="admin-stat-card"><p className="text-xs text-slate-400">Pending Withdrawals</p><p className="text-lg font-bold text-yellow-400">{stats.pending_withdrawals}</p></div>
        </div>
      )}
      <div className="flex gap-2 mb-4 flex-wrap">
        {["pending", "approved", "rejected", ""].map(s => (
          <button key={s} className={`admin-filter-btn ${filter === s ? "active" : ""}`} onClick={() => setFilter(s)}>
            {s || "All"}
          </button>
        ))}
        <select className="admin-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          <option value="deposit">Deposits</option>
          <option value="withdraw">Withdrawals</option>
        </select>
      </div>
      {loading ? <Loader2 className="animate-spin text-[#d4af37] mx-auto" /> : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Date</th><th>User</th><th>Type</th><th>Amount</th><th>Status</th><th>Details</th><th>Actions</th></tr></thead>
            <tbody>
              {txns.map((t, i) => (
                <tr key={i} data-testid={`admin-txn-${i}`}>
                  <td className="text-xs">{new Date(t.created_at).toLocaleString()}</td>
                  <td>{t.user_id || t.phone}</td>
                  <td><span className={`type-badge ${t.type}`}>{t.type}</span></td>
                  <td className="font-bold">Rs.{t.amount?.toFixed(2)}</td>
                  <td><span className={`status-badge-admin ${t.status}`}>{t.status}</span></td>
                  <td className="text-xs">
                    {t.bank_details && <span>Bank: {t.bank_details.bank_name} {t.bank_details.account_number}</span>}
                    {t.receipt_path && (
                      <button className="admin-action-btn approve ml-1" onClick={() => viewReceipt(t.receipt_path)} data-testid={`view-receipt-${i}`} title="View Receipt">
                        <Eye size={14} />
                      </button>
                    )}
                  </td>
                  <td>
                    {t.status === "pending" && (
                      <div className="flex gap-1">
                        <button className="admin-action-btn approve" onClick={() => handleAction(t.txn_id, "approve")} data-testid={`approve-${i}`}>
                          <Check size={14} />
                        </button>
                        <button className="admin-action-btn reject" onClick={() => handleAction(t.txn_id, "reject")} data-testid={`reject-${i}`}>
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Products Tab
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", price: "", maturity_days: "", daily_earning: "", total_profit: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchProducts(); }, []);
  const fetchProducts = async () => {
    try { const { data } = await axios.get(`${API}/admin/products`); setProducts(data); } catch { }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/products`, {
        name: form.name, price: parseFloat(form.price), maturity_days: parseInt(form.maturity_days),
        daily_earning: parseFloat(form.daily_earning), total_profit: parseFloat(form.total_profit)
      });
      toast.success("Product created");
      setForm({ name: "", price: "", maturity_days: "", daily_earning: "", total_profit: "" });
      fetchProducts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/admin/products/${id}`); toast.success("Deleted"); fetchProducts(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-products-tab">
      <h2 className="text-xl font-bold text-white mb-4">Product Management</h2>
      <form onSubmit={handleCreate} className="admin-form mb-6" data-testid="product-create-form">
        <div className="admin-form-grid">
          <input className="auth-input" placeholder="Product Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="product-name" />
          <input className="auth-input" type="number" placeholder="Price (Rs.)" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required data-testid="product-price" />
          <input className="auth-input" type="number" placeholder="Maturity Days" value={form.maturity_days} onChange={e => setForm({...form, maturity_days: e.target.value})} required data-testid="product-days" />
          <input className="auth-input" type="number" placeholder="Daily Earning" value={form.daily_earning} onChange={e => setForm({...form, daily_earning: e.target.value})} required data-testid="product-daily" />
          <input className="auth-input" type="number" placeholder="Total Profit" value={form.total_profit} onChange={e => setForm({...form, total_profit: e.target.value})} required data-testid="product-profit" />
        </div>
        <button type="submit" className="btn-gold mt-3" disabled={loading} data-testid="product-submit">
          {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : <><Plus size={16} className="inline mr-1" /> Add Product</>}
        </button>
      </form>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead><tr><th>Name</th><th>Price</th><th>Days</th><th>Daily</th><th>Total Profit</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {products.map((p, i) => (
              <tr key={i} data-testid={`admin-product-${i}`}>
                <td>{p.name}</td><td>Rs.{p.price}</td><td>{p.maturity_days}</td><td>Rs.{p.daily_earning}</td>
                <td className="text-[#d4af37]">Rs.{p.total_profit}</td>
                <td><span className={`status-badge-admin ${p.active ? "approved" : "rejected"}`}>{p.active ? "Active" : "Inactive"}</span></td>
                <td><button className="admin-action-btn reject" onClick={() => handleDelete(p.product_id)}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Payment Methods Tab
function PaymentMethodsTab() {
  const [methods, setMethods] = useState([]);
  const [name, setName] = useState("");
  const [upiId, setUpiId] = useState("");
  const [qrFile, setQrFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchMethods(); }, []);
  const fetchMethods = async () => {
    try { const { data } = await axios.get(`${API}/admin/payment-methods`); setMethods(data); } catch { }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name) { toast.error("Name required"); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("upi_id", upiId);
      if (qrFile) formData.append("file", qrFile);
      await axios.post(`${API}/admin/payment-methods`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment method added");
      setName(""); setUpiId(""); setQrFile(null);
      fetchMethods();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/admin/payment-methods/${id}`); toast.success("Removed"); fetchMethods(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-methods-tab">
      <h2 className="text-xl font-bold text-white mb-4">Payment Methods</h2>
      <form onSubmit={handleCreate} className="admin-form mb-6" data-testid="method-create-form">
        <div className="space-y-3">
          <input className="auth-input" placeholder="Method Name (e.g. GPay, PhonePe)" value={name} onChange={e => setName(e.target.value)} required data-testid="method-name" />
          <input className="auth-input" placeholder="UPI ID" value={upiId} onChange={e => setUpiId(e.target.value)} data-testid="method-upi" />
          <div className="file-upload-area">
            <input type="file" accept="image/*" onChange={e => setQrFile(e.target.files[0])} className="file-input" data-testid="method-qr-upload" />
            <Upload size={20} className="text-slate-400" />
            <span className="text-sm text-slate-400">{qrFile ? qrFile.name : "Upload QR Code Image"}</span>
          </div>
        </div>
        <button type="submit" className="btn-gold mt-3" disabled={loading} data-testid="method-submit">
          {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : <><Plus size={16} className="inline mr-1" /> Add Method</>}
        </button>
      </form>
      <div className="space-y-3">
        {methods.map((m, i) => (
          <div key={i} className="admin-method-card" data-testid={`admin-method-${i}`}>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-white font-medium">{m.name}</p>
                {m.upi_id && <p className="text-sm text-[#d4af37]">UPI: {m.upi_id}</p>}
                {m.qr_image_path && <p className="text-xs text-slate-500">QR uploaded</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`status-badge-admin ${m.active !== false ? "approved" : "rejected"}`}>{m.active !== false ? "Active" : "Inactive"}</span>
                <button className="admin-action-btn reject" onClick={() => handleDelete(m.method_id)}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Settings Tab
function SettingsTab() {
  const [settings, setSettings] = useState({ min_deposit: 200, min_withdrawal: 500 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`)
      .then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-settings-tab">
      <h2 className="text-xl font-bold text-white mb-4">App Settings</h2>
      <div className="space-y-4 max-w-md">
        <div>
          <label className="auth-label">Minimum Deposit (Rs.)</label>
          <input type="number" className="auth-input" value={settings.min_deposit}
            onChange={e => setSettings({...settings, min_deposit: parseFloat(e.target.value)})} data-testid="settings-min-deposit" />
        </div>
        <div>
          <label className="auth-label">Minimum Withdrawal (Rs.)</label>
          <input type="number" className="auth-input" value={settings.min_withdrawal}
            onChange={e => setSettings({...settings, min_withdrawal: parseFloat(e.target.value)})} data-testid="settings-min-withdrawal" />
        </div>
        <button className="btn-gold" onClick={handleSave} disabled={loading} data-testid="settings-save">
          {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

// Credentials Tab

// Other Settings Tab (Help Link, Telegram, About Products)
function OtherSettingsTab() {
  const [settings, setSettings] = useState({ help_link: "", telegram_link: "", about_products_content: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/other-settings`)
      .then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/other-settings`, settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-other-settings-tab">
      <h2 className="text-xl font-bold text-white mb-4">Other Settings</h2>
      <div className="space-y-4 max-w-lg">
        <div>
          <label className="auth-label">Help Button Link (floating chat icon URL)</label>
          <input type="url" className="auth-input" placeholder="https://wa.me/919999999999 or any help link"
            value={settings.help_link} onChange={e => setSettings({...settings, help_link: e.target.value})}
            data-testid="other-help-link" />
          <p className="text-xs text-slate-500 mt-1">This link opens when users click the floating help button</p>
        </div>
        <div>
          <label className="auth-label">Telegram Channel Link</label>
          <input type="url" className="auth-input" placeholder="https://t.me/yourchannel"
            value={settings.telegram_link} onChange={e => setSettings({...settings, telegram_link: e.target.value})}
            data-testid="other-telegram-link" />
          <p className="text-xs text-slate-500 mt-1">Shown in popup when users login</p>
        </div>
        <div>
          <label className="auth-label">About Products Content</label>
          <textarea className="auth-input" style={{height: 160, paddingTop: 10}} placeholder="Write about your products here..."
            value={settings.about_products_content} onChange={e => setSettings({...settings, about_products_content: e.target.value})}
            data-testid="other-about-content" />
          <p className="text-xs text-slate-500 mt-1">Shown when users click 'About Products' on home page</p>
        </div>
        <button className="btn-gold" onClick={handleSave} disabled={loading} data-testid="other-settings-save">
          {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

function CredentialsTab() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentPassword) { toast.error("Current password required"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/update-credentials`, {
        current_password: currentPassword,
        new_phone: newPhone || null,
        new_password: newPassword || null
      });
      toast.success("Credentials updated");
      setCurrentPassword(""); setNewPhone(""); setNewPassword("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-credentials-tab">
      <h2 className="text-xl font-bold text-white mb-4">Admin Credentials</h2>
      <form onSubmit={handleUpdate} className="space-y-4 max-w-md">
        <div>
          <label className="auth-label">Current Password (required)</label>
          <input type="password" className="auth-input" value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)} required data-testid="cred-current-pw" />
        </div>
        <div>
          <label className="auth-label">New Phone (optional)</label>
          <input type="tel" className="auth-input" value={newPhone} placeholder="Leave blank to keep current"
            onChange={e => setNewPhone(e.target.value)} data-testid="cred-new-phone" />
        </div>
        <div>
          <label className="auth-label">New Password (optional)</label>
          <input type="password" className="auth-input" value={newPassword} placeholder="Leave blank to keep current"
            onChange={e => setNewPassword(e.target.value)} data-testid="cred-new-pw" />
        </div>
        <button type="submit" className="btn-gold" disabled={loading} data-testid="cred-update">
          {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Update Credentials"}
        </button>
      </form>
    </div>
  );
}

// Top Referrers Tab
function ReferrersTab() {
  const [referrers, setReferrers] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/top-referrers`)
      .then(({ data }) => setReferrers(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-referrers-tab">
      <h2 className="text-xl font-bold text-white mb-4">Top Referrers</h2>
      {referrers.length === 0 ? (
        <p className="text-slate-400">No referrals yet</p>
      ) : (
        <div className="space-y-4">
          {referrers.map((r, i) => (
            <div key={i} className="admin-referrer-card" data-testid={`referrer-${i}`}>
              <div className="flex justify-between items-center mb-3">
                <div>
                  <p className="text-white font-bold">ID: {r.user_id}</p>
                  <p className="text-sm text-slate-400">{r.masked_phone} | Code: {r.invite_code}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#d4af37] font-bold">{r.total_referred} referrals</p>
                  <p className="text-xs text-green-400">Rs.{r.total_commission?.toFixed(2)} earned</p>
                </div>
              </div>
              <div className="space-y-1">
                {r.referred_users?.map((u, j) => (
                  <div key={j} className="flex justify-between text-xs px-2 py-1 bg-black/20 rounded">
                    <span className="text-slate-400">ID: {u.user_id} ({u.masked_phone})</span>
                    <span className="text-slate-500">{new Date(u.joined_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Main Admin Panel
export default function AdminPanel() {
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState("users");

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch { }
    localStorage.removeItem("access_token");
    setAdmin(null);
  };

  if (!admin) return <AdminLogin onLogin={setAdmin} />;

  const renderTab = () => {
    switch (activeTab) {
      case "users": return <UsersTab />;
      case "payments": return <PaymentsTab />;
      case "products": return <ProductsTab />;
      case "methods": return <PaymentMethodsTab />;
      case "settings": return <SettingsTab />;
      case "other": return <OtherSettingsTab />;
      case "credentials": return <CredentialsTab />;
      case "referrers": return <ReferrersTab />;
      default: return <UsersTab />;
    }
  };

  return (
    <div className="admin-layout" data-testid="admin-panel">
      <aside className="admin-sidebar" data-testid="admin-sidebar">
        <div className="admin-sidebar-header">
          <span className="logo-text text-lg">
            <span className="text-white">Admin</span> <span className="text-[#d4af37]">Panel</span>
          </span>
        </div>
        <nav className="admin-nav">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <button key={item.id} className={`admin-nav-item ${activeTab === item.id ? "active" : ""}`}
                onClick={() => setActiveTab(item.id)} data-testid={`admin-nav-${item.id}`}>
                <Icon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <button className="admin-nav-item logout mt-auto" onClick={handleLogout} data-testid="admin-logout">
          <LogOut size={18} /> <span>Logout</span>
        </button>
      </aside>
      <main className="admin-main">
        {renderTab()}
      </main>
    </div>
  );
}
